import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, TableSortLabel, IconButton, Tooltip, Chip, Collapse, Stack, Typography, Divider, Button
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { visuallyHidden } from '@mui/utils';
import { FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import dayjs from 'dayjs';
import ContainersList from './ContainersList';
import ConfirmJobDialog from './ConfirmJobDialog';

const headCells = [
  { id: 'expand', label: '' },
  { id: 'id', label: 'ID' },
  { id: 'jobStatus', label: 'Status' },
  { id: 'startDate', label: 'Start Date' },
  { id: 'latestReturnDate', label: 'LRD' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'booking', label: 'Booking #' },
  { id: 'client', label: 'Client' },
  { id: 'actions', label: '' },
];

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        <FirstPage />
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPage />
      </IconButton>
    </Box>
  );
}

const JobsTable = ({ onConfirmDialogOpenChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openMap, setOpenMap] = useState({});
  const [confirmState, setConfirmState] = useState({ open: false, job: null });
  // Prefer V2 slice if populated; otherwise fallback to legacy manage slice
  const stateV2 = useSelector((s) => s.manageV2.getManage);
  const raw =  stateV2?.object || {};
  // Support wrapped responses like { body: { content, totalElements, ... } } or direct page objects
  const pageData = raw?.body && (Array.isArray(raw.body.content) || typeof raw.body.totalElements === 'number') ? raw.body : raw;
  const content = pageData.content || [];
  const totalElements = pageData.totalElements || 0;

  const page = Number(searchParams.get('PageNumber')) || 0;
  const rowsPerPage = Number(searchParams.get('PageSize')) || 5;
  const order = searchParams.get('SortOrder') || 'desc';
  const orderBy = searchParams.get('SortBy') || 'id';

  const setPage = (val) => { searchParams.set('PageNumber', val); setSearchParams(searchParams); };
  const setRowsPerPage = (val) => { searchParams.set('PageSize', val); setSearchParams(searchParams); };
  const setOrder = (val) => { searchParams.set('SortOrder', val); setSearchParams(searchParams); };
  const setOrderBy = (val) => { searchParams.set('SortBy', val); setSearchParams(searchParams); };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getVehiclesCount = (row) => {
    const containers = row?.containers || [];
    return containers.reduce((acc, c) => acc + (c?.shipmentItems?.length || 0), 0);
  };

  const getLRD = (row) => row?.booking?.latestReturnDate || '';
  const getERD = (row) => row?.booking?.earliestReceivingDate || '';
  const daysLeftToLRD = (row) => {
    const lrd = getLRD(row);
    if (!lrd) return null;
    const diff = dayjs(lrd).diff(dayjs(), 'day');
    return diff;
  };

  const toggleOpen = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

  return (
    <Paper sx={{ width: '100%', borderRadius: 3, boxShadow: '0 2px 12px 0 rgba(11, 60, 93, 0.10)' }}>
      <ConfirmJobDialog
        open={confirmState.open}
        job={confirmState.job}
        onClose={() => { setConfirmState({ open: false, job: null }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(false); }}
        searchParams={searchParams}
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              {headCells.map((h) => (
                <TableCell key={h.id} sortDirection={orderBy === h.id ? order : false} sx={{ whiteSpace: 'nowrap' }}>
                  {h.id !== 'expand' && h.id !== 'actions' ? (
                    <TableSortLabel
                      active={orderBy === h.id}
                      direction={orderBy === h.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, h.id)}
                    >
                      {h.label.toUpperCase()}
                      {orderBy === h.id && (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      )}
                    </TableSortLabel>
                  ) : (
                    h.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {content.map((row) => {
              const vehiclesCount = getVehiclesCount(row);
              const lrd = getLRD(row);
              const erd = getERD(row);
              const daysLeft = daysLeftToLRD(row);

              return (
                <React.Fragment key={row.id}>
                  <TableRow sx={{ '&:hover': { backgroundColor: '#F7F8FA' } }}>
                    <TableCell width={48}>
                      <IconButton aria-label="expand row" size="small" onClick={() => toggleOpen(row.id)}>
                        {openMap[row.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Chip
                        color={row.jobStatus === 'Finished' ? 'success' : row.jobStatus === 'Canceled' ? 'error' : 'warning'}
                        size="small"
                        label={row.jobStatus}
                      />
                    </TableCell>
                    <TableCell>{row.startDate || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={lrd || '-'} variant="outlined" />
                        {typeof daysLeft === 'number' && (
                          <Chip size="small" color={daysLeft < 0 ? 'error' : daysLeft <= 2 ? 'warning' : 'default'} label={`${daysLeft}d`} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={vehiclesCount} sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`ERD ${erd || '-'} | LRD ${lrd || '-'}`} arrow>
                        <span>{row?.booking?.number || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row?.client?.name || '-'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={row.jobStatus === 'Finished' || row.jobStatus === 'Canceled'}
                        onClick={() => { setConfirmState({ open: true, job: row }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(true); }}
                      >
                        Confirm Completed
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length}>
                      <Collapse in={openMap[row.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Shipment Details</Typography>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Vessel</Typography>
                              <Typography variant="body2">{row?.booking?.vesselName || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Destination</Typography>
                              <Typography variant="body2">{row?.booking?.destination?.name || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Port Loading</Typography>
                              <Typography variant="body2">{row?.booking?.portLoading?.name || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Shipping Line</Typography>
                              <Typography variant="body2">{row?.booking?.shippingLine?.name || '-'}</Typography>
                            </Box>
                          </Stack>

                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Containers & Vehicles</Typography>
                            <ContainersList containers={row?.containers || []} compact={false} />
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {content.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        SelectProps={{ native: true }}
        ActionsComponent={TablePaginationActions}
      />
    </Paper>
  );
};

export default JobsTable;
